export function formatFileSize(file: File): string {
  const fileSizeInBytes = file.size;
  const fileSizeInKilobytes = file.size / 1024;
  const fileSizeInMegabytes = fileSizeInBytes / (1024 * 1024);
  return fileSizeInMegabytes > 1
    ? fileSizeInMegabytes.toFixed(2) + ' MB'
    : fileSizeInKilobytes.toFixed(2) + ' KB';
}

/**
 * Sanitizes a folder name by replacing invalid characters with underscores and trimming whitespace.
 * The regular expression /[<>:"/\\|?*]/g matches characters that are not allowed in folder names on most file systems:
 * <, >, :, ", /, \, |, ?, and *.
 *
 * @param {string} name - The folder name to sanitize.
 * @returns {string} The sanitized folder name.
 */
export function sanitizeFolderName(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, '_').trim();
}

export function enumToDisplayText(enumStringValue: string): string {
  // Replace all underscores with spaces and capitalise the first letter of each word.
  // Example: 'some_enum_value' --> 'Some Enum Value'
  return (enumStringValue || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

export const formatTimestampLikeMobile = (date: Date): string => {
  // This is the format used in the mobile version.
  const now = date || new Date();
  const timezoneOffset = now.getTimezoneOffset(); // Note: getTimezoneOffset returns negative values for positive offsets
  const hours = Math.floor(Math.abs(timezoneOffset) / 60);
  const minutes = Math.abs(timezoneOffset) % 60;
  const sign = timezoneOffset <= 0 ? '+' : '-'; // Reversed because getTimezoneOffset is opposite

  // Format date as M/D/YYYY
  const month = now.getMonth() + 1;
  const day = now.getDate();
  const year = now.getFullYear();

  // Format time as H:MM (24-hour format)
  const hour = now.getHours();
  const minute = now
    .getMinutes()
    .toString()
    .padStart(2, '0');

  return `${month}/${day}/${year} ${hour}:${minute} (GMT${sign}${hours
    .toString()
    .padStart(2, '0')}:${minutes.toString().padStart(2, '0')})`;
};

/**
 * Gets the user's timezone display name for use in CSV export notes.
 * @returns {string} The timezone display name (e.g., "User's Time Zone" or "GMT+05:30")
 */
export const getUserTimezoneDisplayName = (): string => {
  try {
    // Try to get the timezone name from Intl.DateTimeFormat
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezoneName) {
      return timezoneName;
    }
  } catch (error) {
    // Fallback if timezone detection fails
  }
  // Fallback to GMT offset format
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset();
  const hours = Math.floor(Math.abs(timezoneOffset) / 60);
  const minutes = Math.abs(timezoneOffset) % 60;
  const sign = timezoneOffset <= 0 ? '+' : '-';
  return `GMT${sign}${hours
    .toString()
    .padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
